import { useCallback, useEffect, useState } from 'react'
import { fetchBots, removeBot, setBotActive, type BotOut } from './api'

/**
 * Bot listesi ve eylemleri — iki ekranın ortak kaynağı: masaüstündeki
 * `BotsTab` (seçili kontratın botları) ve mobildeki `BotsPage` (hepsi).
 *
 * Ortaklaştırmanın sebebi eylemler: duraklat/kaldır sonrası listeyi tazeleme ve
 * "bu botta işlem sürüyor" kilidi iki yerde ayrı yazılsaydı zamanla ayrışırdı.
 *
 * Liste 3 sn'de bir yoklanıyor; WebSocket'e bağlanmadı (bilerek): bot durumu
 * saniyelik piyasa verisi değil, yoklama yetiyor ve soket sözleşmesine alan
 * eklemek gerekmiyor.
 */

/** Tüm kontratların botları. Kontrat anahtarı olamayacağı için çakışmaz. */
export const TUM_KONTRATLAR = '*'

/** Şablon adının liste hâli — satıra/karta tam ad sığmıyor. */
export const BOT_KISA_TIP: Record<string, string> = {
  'ID-TRADING-AL-VE-SAT': 'AL-SAT',
  'ID-TRADING-SAT-VE-AL': 'SAT-AL',
  'BOTTOM-TOP': 'B-TOP',
  'BEST-BUYER': 'B-BUY',
  'BEST-SELLER': 'B-SELL',
}

const YOKLAMA_MS = 3000

/**
 * @param kapsam Kontrat anahtarı → yalnız onun botları; `TUM_KONTRATLAR` →
 *   hepsi; `null` → istek atılmaz, liste boş (kontrat seçilmemiş hâli).
 * @param silinmisler Kaldırılmış botlar da gelsin mi (Botlar sayfasındaki
 *   "pasifler" anahtarı). Kapalıyken sunucu veritabanına hiç gitmez.
 */
export function useBots(kapsam: string | null, silinmisler = false) {
  const [botlar, setBotlar] = useState<BotOut[]>([])
  const [hata, setHata] = useState<string | null>(null)
  //: Üzerinde işlem süren bot — çift dokunuşu ve yarışan istekleri önler.
  const [mesgul, setMesgul] = useState<string | null>(null)

  const yenile = useCallback(async () => {
    if (kapsam == null) {
      setBotlar([])
      return
    }
    try {
      setBotlar(await fetchBots(
        kapsam === TUM_KONTRATLAR ? undefined : kapsam, silinmisler))
      setHata(null)
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Bot listesi alınamadı')
    }
  }, [kapsam, silinmisler])

  // Yoklama sekme ARKA PLANDAYKEN duruyor: telefon cebe girdiğinde saniyede bir
  // istek atmak pili ve veriyi boşa harcıyor. Öne dönüldüğünde önce anında bir
  // tazeleme (ekrandaki veri bayat olabilir), sonra sayaç yeniden kuruluyor.
  useEffect(() => {
    yenile()

    let sayac: number | undefined
    const dur = () => {
      if (sayac != null) {
        window.clearInterval(sayac)
        sayac = undefined
      }
    }
    const basla = () => {
      if (sayac == null) sayac = window.setInterval(yenile, YOKLAMA_MS)
    }
    const gorunurluk = () => {
      if (document.hidden) {
        dur()
      } else {
        yenile()
        basla()
      }
    }

    if (!document.hidden) basla()
    document.addEventListener('visibilitychange', gorunurluk)
    return () => {
      dur()
      document.removeEventListener('visibilitychange', gorunurluk)
    }
  }, [yenile])

  /** Ortak sarmalayıcı: kilitle → işi yap → listeyi tazele → kilidi bırak. */
  const calistir = useCallback(
    async (bot: BotOut, is: () => Promise<unknown>, dusuncesizMesaj: string) => {
      setMesgul(bot.id)
      try {
        await is()
        await yenile()
      } catch (err) {
        setHata(err instanceof Error ? err.message : dusuncesizMesaj)
      } finally {
        setMesgul(null)
      }
    },
    [yenile],
  )

  /** Duraklat/sürdür. Duraklayan botun emirleri tahtada KALIR (bilerek). */
  const durumDegistir = useCallback(
    (bot: BotOut) => calistir(bot, () => setBotActive(bot.id, !bot.active), 'Bot güncellenemedi'),
    [calistir],
  )

  /**
   * Kaldır — açık emirleri iptal eder, kayıt geçmişi yerinde kalır.
   *
   * Onay SORMAZ: geri alınamaz bir işlem ama onayı çağıran ekran kendi diliyle
   * alıyor (masaüstünde `window.confirm`, mobilde alttan çıkan panel).
   */
  const kaldir = useCallback(
    (bot: BotOut) => calistir(bot, () => removeBot(bot.id), 'Bot kaldırılamadı'),
    [calistir],
  )

  return { botlar, hata, mesgul, yenile, durumDegistir, kaldir }
}
